import { API_URL, API_BASE_URL } from "../config/api";

class ApiService {
  constructor() {
    // For API routes, always use /api prefix
    this.baseURL = `${API_BASE_URL}/api`;
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // Helper method to handle API responses
  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Network error" }));

      // Check for token expiration
      if (
        errorData.code === "TOKEN_EXPIRED" ||
        errorData.error === "Token expired"
      ) {
        console.warn("⚠️ Token expired - clearing session");
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        // Redirect to login page
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }

        throw new Error("Your session has expired. Please login again.");
      }

      // Check for invalid token
      if (errorData.code === "INVALID_TOKEN" || response.status === 401) {
        console.warn("⚠️ Invalid token - clearing session");
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
      }

      const error = new Error(errorData.error || `HTTP ${response.status}`);
      error.data = errorData; // Attach full error data including existingCategory
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  // Auth APIs
  async adminLogin(email, password) {
    const response = await fetch(`${this.baseURL}/auth/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    return this.handleResponse(response);
  }

  async managerLogin(email, password) {
    const response = await fetch(`${this.baseURL}/auth/manager/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    return this.handleResponse(response);
  }

  async customerManagerLogin(email, password) {
    const response = await fetch(
      `${this.baseURL}/auth/customer-manager/login`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      },
    );
    return this.handleResponse(response);
  }

  async salesExecutiveLogin(email, password) {
    // Sales executives use the admin login endpoint
    const response = await fetch(`${this.baseURL}/auth/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    return this.handleResponse(response);
  }

  async customerLogin(email, password) {
    const response = await fetch(`${this.baseURL}/auth/customer/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    return this.handleResponse(response);
  }

  // Legacy login method for backward compatibility
  async login(email, password) {
    // Only try admin login - customer login should be handled separately
    return await this.adminLogin(email, password);
  }

  async customerRegister(userData) {
    const response = await fetch(`${this.baseURL}/auth/customer/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });
    return this.handleResponse(response);
  }

  // Legacy register method for backward compatibility
  async register(userData) {
    return this.customerRegister(userData);
  }

  async createAdmin(adminData) {
    const response = await fetch(`${this.baseURL}/users`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(adminData),
    });
    return this.handleResponse(response);
  }

  // Company API
  async getCompanyDetails() {
    const response = await fetch(`${this.baseURL}/company`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getProfile() {
    const response = await fetch(`${this.baseURL}/auth/profile`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async updateProfile(profileData) {
    const response = await fetch(`${this.baseURL}/auth/profile`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(profileData),
    });
    return this.handleResponse(response);
  }

  // User Management APIs
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/users?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Get portal users (AdminUser table)
  async getPortalUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/users?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Get customer users (Customer table)
  async getCustomerUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/customers?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Download customer upload template
  async downloadCustomerTemplate() {
    const response = await fetch(`${this.baseURL}/customers/template`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Failed to download template");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customer_upload_template.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  // Bulk upload customers from CSV
  async bulkUploadCustomers(file) {
    const formData = new FormData();
    formData.append("csvFile", file);

    const response = await fetch(`${this.baseURL}/customers/bulk-upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        // Don't set Content-Type header - let browser set it with boundary for FormData
      },
      body: formData,
    });

    return this.handleResponse(response);
  }

  async getSalesExecutives() {
    const response = await fetch(`${this.baseURL}/users/sales-executives`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getUser(id) {
    const response = await fetch(`${this.baseURL}/users/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async updateUser(id, userData) {
    const response = await fetch(`${this.baseURL}/users/${id}`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    return this.handleResponse(response);
  }

  async deleteUser(id) {
    const response = await fetch(`${this.baseURL}/users/${id}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async approveUser(id) {
    const response = await fetch(`${this.baseURL}/users/${id}/approve`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async rejectUser(id, reason) {
    const response = await fetch(`${this.baseURL}/users/${id}/reject`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });
    return this.handleResponse(response);
  }

  // Product APIs with advanced B2B features
  async getProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/products?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Public product list (for customer registration)
  async getPublicProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString
      ? `${this.baseURL}/products/public?${queryString}`
      : `${this.baseURL}/products/public`;
    const response = await fetch(url);
    return this.handleResponse(response);
  }

  async getProduct(id) {
    const response = await fetch(`${this.baseURL}/products/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createProduct(productData) {
    const response = await fetch(`${this.baseURL}/products`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(productData),
    });
    return this.handleResponse(response);
  }

  async updateProduct(id, productData) {
    const response = await fetch(`${this.baseURL}/products/${id}`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(productData),
    });
    return this.handleResponse(response);
  }

  async deleteProduct(id) {
    const response = await fetch(`${this.baseURL}/products/${id}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getProductCategories() {
    const response = await fetch(`${this.baseURL}/products/categories/list`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getPublicCategories() {
    const response = await fetch(`${this.baseURL}/categories/public`);
    return this.handleResponse(response);
  }

  async getCategories() {
    const response = await fetch(`${this.baseURL}/categories/flat`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getNextCategoryCode() {
    const response = await fetch(`${this.baseURL}/categories/next-code`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createCategory(categoryData) {
    const response = await fetch(`${this.baseURL}/categories`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(categoryData),
    });
    return this.handleResponse(response);
  }

  async updateCategory(id, categoryData) {
    const response = await fetch(`${this.baseURL}/categories/${id}`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(categoryData),
    });
    return this.handleResponse(response);
  }

  async deleteCategory(id) {
    const response = await fetch(`${this.baseURL}/categories/${id}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async calculateProductPrice(productId, quantity) {
    const response = await fetch(
      `${this.baseURL}/products/${productId}/calculate-price`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ quantity }),
      },
    );
    return this.handleResponse(response);
  }

  // Bulk Pricing APIs
  async getBulkPricing(productId) {
    const response = await fetch(
      `${this.baseURL}/bulk-pricing/product/${productId}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getAllBulkPricing(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/bulk-pricing?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async createBulkPricing(bulkPricingData) {
    const response = await fetch(`${this.baseURL}/bulk-pricing`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bulkPricingData),
    });
    return this.handleResponse(response);
  }

  async updateBulkPricing(id, bulkPricingData) {
    const response = await fetch(`${this.baseURL}/bulk-pricing/${id}`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(bulkPricingData),
    });
    return this.handleResponse(response);
  }

  async deleteBulkPricing(id) {
    const response = await fetch(`${this.baseURL}/bulk-pricing/${id}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async calculateBulkPricing(productId, quantity) {
    const response = await fetch(`${this.baseURL}/bulk-pricing/calculate`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ productId, quantity }),
    });
    return this.handleResponse(response);
  }

  // Order APIs with advanced B2B features
  async getOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/orders?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getAdminDashboardOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/orders/admin/dashboard?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getCustomerOrders() {
    const response = await fetch(`${this.baseURL}/orders/my-orders`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Draft order APIs
  async getDraftOrders() {
    const response = await fetch(`${this.baseURL}/orders/my-orders/drafts`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async saveDraft(orderData) {
    const response = await fetch(`${this.baseURL}/orders/draft`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData),
    });
    return this.handleResponse(response);
  }

  async updateDraft(orderId, orderData) {
    const response = await fetch(`${this.baseURL}/orders/${orderId}/draft`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData),
    });
    return this.handleResponse(response);
  }

  async submitDraft(orderId) {
    const response = await fetch(`${this.baseURL}/orders/${orderId}/submit`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Manager approval APIs
  async getPendingApprovalOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/orders/pending-approval?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getPendingApprovals(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/orders/customer/pending-approval?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getManagerPendingApprovalOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/orders/manager/pending-approval?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async approveOrder(orderId, internalNotes = "") {
    const response = await fetch(`${this.baseURL}/orders/${orderId}/approve`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ internalNotes }),
    });
    return this.handleResponse(response);
  }

  async managerApproveOrder(orderId, internalNotes = "") {
    const response = await fetch(
      `${this.baseURL}/orders/manager/${orderId}/approve`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ internalNotes }),
      },
    );
    return this.handleResponse(response);
  }

  async rejectOrder(orderId, rejectionReason, internalNotes = "") {
    const response = await fetch(`${this.baseURL}/orders/${orderId}/reject`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ rejectionReason, internalNotes }),
    });
    return this.handleResponse(response);
  }

  async managerRejectOrder(orderId, rejectionReason, internalNotes = "") {
    const response = await fetch(
      `${this.baseURL}/orders/manager/${orderId}/reject`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ rejectionReason, internalNotes }),
      },
    );
    return this.handleResponse(response);
  }

  // Customer Manager Order APIs
  async getCustomerManagerPendingApprovalOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/orders/customer-manager/pending-approval?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async customerManagerApproveOrder(orderId, internalNotes = "") {
    const response = await fetch(
      `${this.baseURL}/orders/customer-manager/${orderId}/approve`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ internalNotes }),
      },
    );
    return this.handleResponse(response);
  }

  async customerManagerRejectOrder(
    orderId,
    rejectionReason,
    internalNotes = "",
  ) {
    const response = await fetch(
      `${this.baseURL}/orders/customer-manager/${orderId}/reject`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ rejectionReason, internalNotes }),
      },
    );
    return this.handleResponse(response);
  }

  async getOrder(id) {
    const response = await fetch(`${this.baseURL}/orders/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createOrder(orderData) {
    const response = await fetch(`${this.baseURL}/orders`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData),
    });
    return this.handleResponse(response);
  }

  async updateOrderStatus(id, statusData) {
    const response = await fetch(`${this.baseURL}/orders/${id}/status`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(statusData),
    });
    return this.handleResponse(response);
  }

  async deleteOrder(id) {
    const response = await fetch(`${this.baseURL}/orders/${id}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getOrderStats() {
    const response = await fetch(`${this.baseURL}/orders/stats/overview`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Invoice methods
  async getInvoices() {
    const response = await fetch(`${this.baseURL}/invoices`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getInvoice(id) {
    const response = await fetch(`${this.baseURL}/invoices/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async createInvoice(invoiceData) {
    const response = await fetch(`${this.baseURL}/invoices`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(invoiceData),
    });
    return this.handleResponse(response);
  }

  async createPartialInvoice(invoiceData) {
    const response = await fetch(`${this.baseURL}/invoices/partial`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(invoiceData),
    });
    return this.handleResponse(response);
  }

  async getPartialInvoiceStatus(orderId) {
    const response = await fetch(
      `${this.baseURL}/invoices/order/${orderId}/partial-status`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async updateInvoiceStatus(id, status) {
    const response = await fetch(`${this.baseURL}/invoices/${id}/status`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return this.handleResponse(response);
  }

  // Payment APIs
  async createPayment(paymentData) {
    const response = await fetch(
      `${this.baseURL}/invoices/${paymentData.invoiceId}/payments`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(paymentData),
      },
    );
    return this.handleResponse(response);
  }

  async getPayments(invoiceId) {
    const response = await fetch(
      `${this.baseURL}/invoices/${invoiceId}/payments`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  // Delivery APIs
  async getDeliveries(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/delivery?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getDelivery(id) {
    console.log("getDelivery called with ID:", id);
    console.log("Auth headers:", this.getAuthHeaders());

    try {
      // Temporarily remove auth headers for testing
      const headers = {};
      const url = `${this.baseURL}/delivery/${id}`;
      console.log("Making request to:", url);

      const response = await fetch(url, {
        headers: headers,
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Network error" }));
        console.error("Response error data:", errorData);
        throw new Error(
          errorData.error ||
            `HTTP ${response.status}: Failed to fetch delivery order`,
        );
      }

      const data = await response.json();
      console.log("Response data:", data);
      return data;
    } catch (error) {
      console.error("getDelivery error:", error);
      throw error;
    }
  }

  async updateDeliveryStatus(id, statusData) {
    const response = await fetch(`${this.baseURL}/delivery/${id}/status`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(statusData),
    });
    return this.handleResponse(response);
  }

  async createDeliveryOrder(deliveryOrderData) {
    const response = await fetch(`${this.baseURL}/delivery`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(deliveryOrderData),
    });
    return this.handleResponse(response);
  }

  // Report APIs
  async getReports(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/reports?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getSalesReport(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/reports/sales?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getInventoryReport() {
    const response = await fetch(`${this.baseURL}/reports/inventory`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getCustomerReport() {
    const response = await fetch(`${this.baseURL}/reports/customers`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getCollectionsReport(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/reports/collections?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async refreshAllReports() {
    const response = await fetch(`${this.baseURL}/reports/refresh-all`, {
      method: "POST",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Utility methods
  async uploadFile(file, type = "image") {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    const response = await fetch(`${this.baseURL}/upload`, {
      method: "POST",
      headers: {
        ...this.getAuthHeaders(),
        // Remove Content-Type to let browser set it with boundary
      },
      body: formData,
    });
    return this.handleResponse(response);
  }

  // Customer Credit APIs
  async getCustomerCredits(customerId) {
    const response = await fetch(
      `${this.baseURL}/users/${customerId}/credits`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async addCustomerCredit(creditData) {
    const response = await fetch(
      `${this.baseURL}/users/${creditData.customerId}/credits`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(creditData),
      },
    );
    return this.handleResponse(response);
  }

  // Recurring Order APIs
  async getRecurringOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/recurring-orders?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async createRecurringOrder(orderData) {
    const response = await fetch(`${this.baseURL}/recurring-orders`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData),
    });
    return this.handleResponse(response);
  }

  async updateRecurringOrder(id, orderData) {
    const response = await fetch(`${this.baseURL}/recurring-orders/${id}`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData),
    });
    return this.handleResponse(response);
  }

  async deleteRecurringOrder(id) {
    const response = await fetch(`${this.baseURL}/recurring-orders/${id}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Dashboard APIs
  async getDashboardReport() {
    const response = await fetch(`${this.baseURL}/reports/dashboard`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Advanced Analytics APIs for Business Intelligence
  async getSalesAnalytics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/reports/sales?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getCustomerAnalytics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/reports/customers?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getProductPerformance(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/orders?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getRevenueAnalytics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/reports/dashboard?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getPaymentAnalytics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/invoices?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getOrderTrends(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/orders?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getCustomers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${this.baseURL}/customers?${queryString}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getPendingUsers() {
    const response = await fetch(`${this.baseURL}/customers/pending`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async updateUserStatus(userId, status) {
    const response = await fetch(`${this.baseURL}/users/${userId}/status`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return this.handleResponse(response);
  }

  // Customer approval/rejection
  async approveCustomer(customerId, salesExecutiveId = null, managerId = null) {
    const body = {};
    if (salesExecutiveId !== undefined && salesExecutiveId !== null) {
      body.salesExecutiveId = salesExecutiveId;
    }
    if (managerId !== undefined && managerId !== null) {
      body.managerId = managerId;
    }

    const response = await fetch(
      `${this.baseURL}/customers/${customerId}/approve`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(body),
      },
    );
    return this.handleResponse(response);
  }

  async rejectCustomer(customerId, rejectionReason) {
    const response = await fetch(
      `${this.baseURL}/customers/${customerId}/reject`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ rejectionReason }),
      },
    );
    return this.handleResponse(response);
  }

  // Assign manager to customer
  async assignManagerToCustomer(customerId, managerId) {
    const response = await fetch(
      `${this.baseURL}/customers/${customerId}/assign-manager`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ managerId }),
      },
    );
    return this.handleResponse(response);
  }

  // Assign sales executive to customer
  async assignSalesExecutiveToCustomer(customerId, salesExecutiveId) {
    const response = await fetch(
      `${this.baseURL}/customers/${customerId}/assign-sales-executive`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ salesExecutiveId }),
      },
    );
    return this.handleResponse(response);
  }

  // Reset customer password (admin only)
  async resetCustomerPassword(customerId) {
    const response = await fetch(
      `${this.baseURL}/customers/${customerId}/reset-password`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  // Reset portal user password (admin only)
  async resetPortalUserPassword(userId) {
    const response = await fetch(
      `${this.baseURL}/users/${userId}/reset-password`,
      {
        method: "PATCH",
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  // Cart APIs
  async getCart() {
    const response = await fetch(`${this.baseURL}/cart`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async addToCart(productId, quantity, skuId = null) {
    const response = await fetch(`${this.baseURL}/cart/items`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ productId, quantity, skuId }),
    });
    return this.handleResponse(response);
  }

  async updateCartItem(itemId, quantity) {
    const response = await fetch(`${this.baseURL}/cart/items/${itemId}`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ quantity }),
    });
    return this.handleResponse(response);
  }

  async removeFromCart(itemId) {
    const response = await fetch(`${this.baseURL}/cart/items/${itemId}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async clearCart() {
    const response = await fetch(`${this.baseURL}/cart`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getCartSummary() {
    const response = await fetch(`${this.baseURL}/cart/summary`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Customer change password
  async changeCustomerPassword(newPassword) {
    const response = await fetch(
      `${this.baseURL}/auth/customer/change-password`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ newPassword }),
      },
    );
    return this.handleResponse(response);
  }

  // Admin change password
  async changeAdminPassword(currentPassword, newPassword) {
    const token = localStorage.getItem("token");
    const response = await fetch(`${this.baseURL}/auth/admin/change-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, currentPassword, newPassword }),
    });
    return this.handleResponse(response);
  }

  // Salesman change password
  async changeSalesmanPassword(currentPassword, newPassword) {
    const token = localStorage.getItem("token");
    const response = await fetch(
      `${this.baseURL}/auth/salesman/change-password`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, currentPassword, newPassword }),
      },
    );
    return this.handleResponse(response);
  }

  // Request password reset
  async requestPasswordReset(email) {
    const response = await fetch(`${this.baseURL}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return this.handleResponse(response);
  }

  // Reset password with token
  async resetPassword(token, newPassword) {
    const response = await fetch(`${this.baseURL}/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newPassword }),
    });
    return this.handleResponse(response);
  }

  // Verify email with token
  async verifyEmail(token) {
    const response = await fetch(`${this.baseURL}/auth/verify-email/${token}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return this.handleResponse(response);
  }

  // Resend verification email
  async resendVerification(email) {
    const response = await fetch(`${this.baseURL}/auth/resend-verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return this.handleResponse(response);
  }

  // Generic HTTP methods
  async post(endpoint, data) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async get(endpoint) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async patch(endpoint, data) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  // Customer Profile APIs
  async getCustomerProfile() {
    const response = await fetch(`${this.baseURL}/customers/profile/me`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async updateCustomerProfile(profileData) {
    const response = await fetch(`${this.baseURL}/customers/profile/me`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(profileData),
    });
    return this.handleResponse(response);
  }

  // Customer Invoice APIs
  async getCustomerInvoices() {
    const response = await fetch(`${this.baseURL}/invoices/my-invoices`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getInvoiceById(invoiceId) {
    const response = await fetch(`${this.baseURL}/invoices/${invoiceId}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getInventoryByProduct(productId) {
    const response = await fetch(
      `${this.baseURL}/inventory/product/${productId}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  // Reports APIs
  async getSupplierManagement(params = "") {
    const response = await fetch(
      `${this.baseURL}/reports/supplier-management?${params}`,
      {
        method: "GET",
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getSalesManagement(params = "") {
    console.log("🔍 Calling sales management API with params:", params);
    console.log(
      "🔗 URL:",
      `${this.baseURL}/reports/sales-management?${params}`,
    );
    console.log("🔐 Headers:", this.getAuthHeaders());

    const response = await fetch(
      `${this.baseURL}/reports/sales-management?${params}`,
      {
        method: "GET",
        headers: this.getAuthHeaders(),
      },
    );

    console.log("📡 Response status:", response.status);
    console.log("📡 Response ok:", response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ API Error:", errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log("📊 Sales management data:", data);
    return data;
  }

  async recordCustomerPayment(paymentData) {
    const response = await fetch(`${this.baseURL}/reports/customer-payment`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(paymentData),
    });
    return this.handleResponse(response);
  }

  // Employee APIs
  async getEmployeeProfile(employeeId) {
    const response = await fetch(
      `${this.baseURL}/payroll/employees/profile/${employeeId}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getEmployeeDashboard() {
    const response = await fetch(
      `${this.baseURL}/payroll/employees/dashboard`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async uploadEmployeeImage(formData) {
    const response = await fetch(
      `${this.baseURL}/payroll/employees/upload-image`,
      {
        method: "POST",
        headers: {
          ...this.getAuthHeaders(),
          // Remove Content-Type to let browser set it with boundary
        },
        body: formData,
      },
    );
    return this.handleResponse(response);
  }

  async updateEmployeeProfile(employeeId, profileData) {
    const response = await fetch(
      `${this.baseURL}/payroll/employees/profile/${employeeId}`,
      {
        method: "PUT",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(profileData),
      },
    );
    return this.handleResponse(response);
  }

  async getEmployeeAttendance(employeeId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/payroll/employees/${employeeId}/attendance?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getEmployeePayslips(employeeId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/payroll/employees/${employeeId}/payslips?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async getEmployeeLeaves(employeeId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${this.baseURL}/payroll/employees/${employeeId}/leaves?${queryString}`,
      {
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  async requestLeave(leaveData) {
    const response = await fetch(
      `${this.baseURL}/payroll/employees/request-leave`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(leaveData),
      },
    );
    return this.handleResponse(response);
  }

  async clockInOut(employeeId, action) {
    const response = await fetch(
      `${this.baseURL}/payroll/employees/${employeeId}/clock-${action}`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
      },
    );
    return this.handleResponse(response);
  }
}

const apiService = new ApiService();

// Inventory API
const API_BASE = `${API_BASE_URL}/api/inventory`;

export async function getInventoryItems() {
  const res = await fetch(API_BASE, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch inventory items");
  return res.json();
}

export async function getInventoryItem(id) {
  const res = await fetch(`${API_BASE}/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch inventory item");
  return res.json();
}

export async function createInventoryItem(data) {
  const res = await fetch(API_BASE, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errorData = await res
      .json()
      .catch(() => ({ error: "Failed to create inventory item" }));
    throw new Error(
      errorData.error || errorData.details || "Failed to create inventory item",
    );
  }
  return res.json();
}

export async function updateInventoryItem(id, data) {
  const res = await fetch(`${API_BASE}/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errorData = await res
      .json()
      .catch(() => ({ error: "Failed to update inventory item" }));
    throw new Error(
      errorData.error || errorData.details || "Failed to update inventory item",
    );
  }
  return res.json();
}

export async function deleteInventoryItem(id) {
  const res = await fetch(`${API_BASE}/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to delete inventory item");
  return res.json();
}

export async function addStockMovement(id, data) {
  const res = await fetch(`${API_BASE}/${id}/movement`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to add stock movement");
  return res.json();
}

export async function getMovementLog(id) {
  const res = await fetch(`${API_BASE}/${id}/movements`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch movement log");
  return res.json();
}

// New SKU-based inventory methods
export async function getInventoryBySKU(skuId) {
  const res = await fetch(`${API_BASE}/sku/${skuId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch inventory by SKU");
  return res.json();
}

// Bulk initialize inventory for all SKUs
export async function bulkInitializeInventory() {
  const res = await fetch(`${API_BASE}/bulk-initialize`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  if (!res.ok) {
    const errorData = await res
      .json()
      .catch(() => ({ error: "Failed to bulk initialize inventory" }));
    throw new Error(
      errorData.error ||
        errorData.details ||
        "Failed to bulk initialize inventory",
    );
  }
  return res.json();
}

// Get SKUs without inventory (preview for bulk initialization)
export async function getSkusWithoutInventory() {
  const res = await fetch(`${API_BASE}/skus-without-inventory`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch SKUs without inventory");
  return res.json();
}

export async function allocateInventory(items, deliveryOrderId) {
  const res = await fetch(`${API_BASE}/allocate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ items, deliveryOrderId }),
  });
  if (!res.ok) {
    const errorData = await res
      .json()
      .catch(() => ({ error: "Failed to allocate inventory" }));
    throw new Error(
      errorData.error || errorData.details || "Failed to allocate inventory",
    );
  }
  return res.json();
}

export async function releaseInventoryAllocation(deliveryOrderId) {
  const res = await fetch(`${API_BASE}/release`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ deliveryOrderId }),
  });
  if (!res.ok) {
    const errorData = await res
      .json()
      .catch(() => ({ error: "Failed to release inventory allocation" }));
    throw new Error(
      errorData.error ||
        errorData.details ||
        "Failed to release inventory allocation",
    );
  }
  return res.json();
}

export async function getInventoryMovements(inventoryItemId) {
  const res = await fetch(`${API_BASE}/${inventoryItemId}/movements`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch inventory movements");
  return res.json();
}

export async function createInventoryMovement(data) {
  const res = await fetch(`${API_BASE}/movements`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create inventory movement");
  return res.json();
}

export async function getLowStockReport() {
  const res = await fetch(`${API_BASE}/report/low-stock`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch low stock report");
  return res.json();
}

export async function getExpiringItems() {
  const res = await fetch(`${API_BASE}/report/expiring`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch expiring items");
  return res.json();
}

export async function getInventoryReports() {
  const res = await fetch(`${API_BASE}/reports`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch inventory reports");
  return res.json();
}

export async function getAllProducts() {
  const res = await fetch(`${API_BASE_URL}/api/products`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch products");
  return res.json();
}

// Supplier Management API
export const createSupplier = async (supplierData) => {
  const response = await fetch(`${API_BASE_URL}/api/suppliers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(supplierData),
  });

  if (!response.ok) throw new Error("Failed to create supplier");
  return response.json();
};

export const getSuppliers = async () => {
  const response = await fetch(`${API_BASE_URL}/api/suppliers`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch suppliers");
  return response.json();
};

export const getSupplierByName = async (name) => {
  const suppliers = await getSuppliers();
  return suppliers.find((s) => s.name.toLowerCase() === name.toLowerCase());
};

export const uploadInventoryFileForSupplierExtraction = async (formData) => {
  const response = await fetch(
    `${API_BASE_URL}/api/suppliers/extract-from-inventory`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Network error" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
};

export const createPurchaseOrder = async (poData) => {
  const response = await fetch(`${API_BASE_URL}/api/purchase-orders`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(poData),
  });

  if (!response.ok) throw new Error("Failed to create purchase order");
  return response.json();
};

export const createSupplierInvoice = async (invoiceData) => {
  const response = await fetch(`${API_BASE_URL}/api/supplier-invoices`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(invoiceData),
  });

  if (!response.ok) throw new Error("Failed to create supplier invoice");
  return response.json();
};

// Get supplier invoices
export const getSupplierInvoices = async (filters = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    if (filters[key]) queryParams.append(key, filters[key]);
  });

  const url = `${API_BASE_URL}/api/supplier-invoices?${queryParams}`;
  console.log("🔗 API URL:", url);
  console.log("🔍 Filters:", filters);

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  console.log("📡 Response status:", response.status);

  if (!response.ok) {
    const errorText = await response.text();
    console.error("❌ API Error:", errorText);
    throw new Error("Failed to fetch supplier invoices");
  }

  const data = await response.json();
  console.log("📊 API Response data:", data);
  return data;
};

// Get supplier invoice by ID
export const getSupplierInvoiceById = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/supplier-invoices/${id}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch supplier invoice");
  return response.json();
};

// Record supplier payment
export const recordSupplierPayment = async (invoiceId, paymentData) => {
  const response = await fetch(
    `${API_BASE_URL}/api/supplier-invoices/${invoiceId}/payments`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(paymentData),
    },
  );

  if (!response.ok) throw new Error("Failed to record payment");
  return response.json();
};

// Get supplier invoices with detailed information
export const getSupplierInvoicesDetailed = async (supplierId, filters = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    if (filters[key]) queryParams.append(key, filters[key]);
  });

  const response = await fetch(
    `${API_BASE_URL}/api/supplier-invoices/supplier/${supplierId}?${queryParams}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch supplier invoices");
  return response.json();
};

// Get pending payments report
export const getPendingPaymentsReport = async () => {
  const response = await fetch(
    `${API_BASE_URL}/api/supplier-invoices/reports/pending-payments`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch pending payments report");
  return response.json();
};

// Get payment summary report
export const getPaymentSummaryReport = async (filters = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    if (filters[key]) queryParams.append(key, filters[key]);
  });

  const response = await fetch(
    `${API_BASE_URL}/api/supplier-invoices/reports/payment-summary?${queryParams}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch payment summary report");
  return response.json();
};

// Supplier Master API
export const getSupplierMaster = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key]) queryParams.append(key, params[key]);
  });

  const response = await fetch(
    `${API_BASE_URL}/api/supplier-master?${queryParams}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch suppliers");
  return response.json();
};

export const createSupplierMaster = async (supplierData) => {
  const response = await fetch(`${API_BASE_URL}/api/supplier-master`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(supplierData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to create supplier");
  }
  return response.json();
};

export const updateSupplierMaster = async (id, supplierData) => {
  const response = await fetch(`${API_BASE_URL}/api/supplier-master/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(supplierData),
  });

  if (!response.ok) throw new Error("Failed to update supplier");
  return response.json();
};

export const deleteSupplierMaster = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/supplier-master/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to delete supplier");
  }
  return response.json();
};

export const getSupplierMasterById = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/supplier-master/${id}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch supplier");
  return response.json();
};

export const verifySupplier = async (id, verificationNotes) => {
  const response = await fetch(
    `${API_BASE_URL}/api/supplier-master/${id}/verify`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ verificationNotes }),
    },
  );

  if (!response.ok) throw new Error("Failed to verify supplier");
  return response.json();
};

export const addSupplierPerformance = async (id, performanceData) => {
  const response = await fetch(
    `${API_BASE_URL}/api/supplier-master/${id}/performance`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(performanceData),
    },
  );

  if (!response.ok) throw new Error("Failed to add performance evaluation");
  return response.json();
};

export const getSupplierPerformanceReport = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key]) queryParams.append(key, params[key]);
  });

  const response = await fetch(
    `${API_BASE_URL}/api/supplier-master/reports/performance?${queryParams}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch performance report");
  return response.json();
};

// Territory Customers API
export const getTerritoryCustomers = async (territoryId, params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key]) queryParams.append(key, params[key]);
  });

  const response = await fetch(
    `${API_BASE_URL}/api/territories/${territoryId}/customers?${queryParams}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch territory customers");
  return response.json();
};

// Company API
export const getCompanyDetails = async () => {
  const response = await fetch(`${API_BASE_URL}/api/company`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch company details");
  return response.json();
};

export default apiService;
